/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */
import type { Method } from 'axios'

import config from '@src/config'
import type { Integration, LegacyIntegration } from '@src/service/model'
import type CallDeposition from '@src/service/model/integration/CallDeposition'
import type { ClioIntegrationType } from '@src/service/model/integration/ClioIntegrationModel'
import type ContactImport from '@src/service/model/integration/ContactImport'
import type CreatedBy from '@src/service/model/integration/CreatedBy'
import type { RawSalesforceIntegration } from '@src/service/model/integration/SalesforceIntegrationModel'

import type Transport from '.'
import { HttpTransaction } from './transaction'

interface IIntegrationBase {
  id: string
  type: string
  orgId: string
  appId: string
  userId: string
  createdAt: Date
  updatedAt: Date
  createdBy: CreatedBy | null
}

interface IHubspotIntegration extends IIntegrationBase {
  type: 'hubspot'
  hubspotUser: string
  resourceIds: string[]
  refreshToken: string
  accessToken: string
  accessTokenExpiresAt: Date
  contactProperties: string[]
  callDepositions: CallDeposition[]
  contactImport: ContactImport
}

interface IGongIntegration extends IIntegrationBase {
  accessToken: string
  accessTokenExpiresAt: Date
  gongUser?: string
  refreshToken: string
  resourceIds: string[]
  scope: string
  sendCallRecordings: { enabled: boolean }
  tokenType: string
  triggers: string[]
  type: 'gong'
  workspaceId: string | null
}

interface IWebhookIntegration extends IIntegrationBase {
  resourceIds: string[]
  events: string[]
  method: Method
  type: 'webhook'
  url: string
}

type IIntegration =
  | IHubspotIntegration
  | RawSalesforceIntegration
  | IGongIntegration
  | IWebhookIntegration

export interface IntegrationEvent {
  incomingMessage: boolean
  answeredIncomingCall: boolean
  missedCallWithoutVoicemail: boolean
  missedCallWithVoicemail: boolean
  aiHandledCall?: boolean
  aiHandledConversation?: boolean
}

export type IntegrationCreateParams = {
  code: string
  redirectUri: string
  resourceIds: string[]
  /** PKCE verifier (RFC 7636); only sent by providers with `pkceEnabled` set. */
  codeVerifier?: string
}

export type IntegrationCreateApiKeyParams = {
  apiKey: string
  resourceIds: string[]
}

export type IntegrationCreateZohoParams = IntegrationCreateParams & {
  location?: string
  accountsServer?: string
}

export type IntegrationCreateClioParams = IntegrationCreateParams & {
  type: ClioIntegrationType
}

export interface CreateSlackAuthParams {
  code: string
  phoneNumberId: string
  events: IntegrationEvent
  redirectUri: string
}

interface IntegrationStatus {
  integrationId: string
  count: number
  status?: 'None' | 'Active' | 'Failed' | 'Succeeded'
  error?: string
}

export default class IntegrationClient {
  constructor(private transport: Transport) {}

  getAll(): Promise<Integration[]> {
    return this.transport.queue(
      new HttpTransaction({
        url: `${config.INTEGRATION_SERVICE_URL}integration`,
      }),
    )
  }

  getForPhoneNumber(phoneNumberId: string): Promise<IIntegration[]> {
    return this.transport.queue(
      new HttpTransaction({
        url: `${config.COMMUNICATION_SERVICE_URL}integration/settings/${phoneNumberId}`,
      }),
    )
  }

  update(integration: Partial<Integration>): Promise<Integration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'put',
        url: `${config.INTEGRATION_SERVICE_URL}integration`,
        body: integration,
      }),
    )
  }

  createAttio(body: IntegrationCreateParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/attio`,
        body,
        retry: false,
      }),
    )
  }

  createHubspot(body: IntegrationCreateParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/hubspot`,
        body,
        retry: false,
      }),
    )
  }

  createJobber(body: IntegrationCreateParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/jobber`,
        body,
        retry: false,
      }),
    )
  }

  createSalesforce(body: IntegrationCreateParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/salesforce`,
        body,
        retry: false,
      }),
    )
  }

  createGong(body: IntegrationCreateParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/gong`,
        body,
        retry: false,
      }),
    )
  }

  createPipedrive(body: IntegrationCreateParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/pipedrive`,
        body,
        retry: false,
      }),
    )
  }

  createZoho(body: IntegrationCreateZohoParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/zoho`,
        body,
        retry: false,
      }),
    )
  }

  createClio(body: IntegrationCreateClioParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/clio`,
        body,
        retry: false,
      }),
    )
  }

  createHighLevel(body: IntegrationCreateParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/highlevel`,
        body,
      }),
    )
  }

  createJobTread(body: IntegrationCreateParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/jobtread`,
        body,
      }),
    )
  }

  createMonday(body: IntegrationCreateParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/monday`,
        body,
      }),
    )
  }

  createHousecallPro(body: IntegrationCreateApiKeyParams): Promise<IIntegration> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.INTEGRATION_SERVICE_URL}provider/housecallpro`,
        body,
        retry: false,
        // Keeps the API key out of the IndexedDB transaction queue.
        skipPersisting: true,
      }),
    )
  }

  delete(id: string): Promise<void> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'delete',
        url: `${config.INTEGRATION_SERVICE_URL}integration/${id}`,
      }),
    )
  }

  status(id: string): Promise<IntegrationStatus> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'get',
        url: `${config.INTEGRATION_SERVICE_URL}integration/${id}/status`,
      }),
    )
  }

  legacy = {
    get: (phoneNumberId: string): Promise<LegacyIntegration[]> => {
      return this.transport.queue(
        new HttpTransaction({
          url: `${config.COMMUNICATION_SERVICE_URL}admin/integration/settings/${phoneNumberId}`,
        }),
      )
    },

    createSlack: (params: CreateSlackAuthParams): Promise<LegacyIntegration> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}admin/integration/slack`,
          body: params,
        }),
      )
    },

    createEmail: (params: Partial<LegacyIntegration>): Promise<LegacyIntegration> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'post',
          url: `${config.COMMUNICATION_SERVICE_URL}admin/integration/email`,
          body: params,
        }),
      )
    },

    update: ({
      id,
      ...settings
    }: Partial<LegacyIntegration>): Promise<LegacyIntegration> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'put',
          url: `${config.COMMUNICATION_SERVICE_URL}admin/integration/settings/${id}`,
          body: settings,
        }),
      )
    },

    delete: (settings: Partial<LegacyIntegration>): Promise<void> => {
      return this.transport.queue(
        new HttpTransaction({
          method: 'delete',
          url: `${config.COMMUNICATION_SERVICE_URL}admin/integration/settings/${settings.id}`,
        }),
      )
    },
  }
}
